import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import PropTypes from 'prop-types';
import TableHeader from 'components/Table/TableHeader';

const initFind = {
  search: '',
  order_by: 'tb.created_at',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
};

const initSummary = {
  quantity: '',
};

function QueryProductCampaign({ productCode, setLoading }) {
  const mod = 'rp_product_query';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [summary, setSummary] = useState(initSummary);
  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);

  const initHeader = [
    { id: 'campaign_code', label: 'Promotion', align: 'left' },
    { id: 'quantity', label: 'จำนวน', align: 'right' },
    { id: 'date_start', label: 'เริ่ม', align: 'center' },
    { id: 'date_stop', label: 'ถึง', align: 'center' },
    { id: 'promotion_type', label: 'Type', align: 'left' },
    { id: 'campaign_quantity', label: 'ปริมาณ', align: 'right' },
    { id: 'price', label: 'ราคา', align: 'right' },
    { id: 'enable', label: 'ON / OFF', align: 'center' },
  ];

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData('OPTIONS', `/${mod}/product_campaign`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setPageTotal(result.total_pages);
        setPageNo(result.page);
        setSummary(result.summary);
      }
      setLoading(false);
    });
  };

  useEffect(() => {
    if (productCode) view({ ...find, search: productCode, page: 1 });
  }, [productCode]);

  const promotionDisplay = (inp) => {
    if (inp === 'A') return <code className='color-success'>A : ซื้อครบจำนวนลดราคา</code>;
    if (inp === 'B') return <code className='color-primary'>B : ซื้อครบมูลค่าได้ของแถม</code>;
    if (inp === 'C') return <code className='color-info'>C : ซื้อครบจำนวนได้ของแถม</code>;
    if (inp === 'D') return <code className='color-danger'>D : ยิ่งซื้อยิ่งลด</code>;
    if (inp === 'E') return <code className='color-warning'>E : ซื้อครบมูลค่าได้ส่วนลด</code>;
    return '';
  };

  const btnPromotion = (row) => {
    return (
      <MDButton
        variant='text'
        color='info'
        sx={{ p: 0, fontSize: '0.9rem', fontWeight: 200, textAlign: 'left' }}
        href={`/campaign/${row.campaign_id}`}
      >
        {row.campaign_code}
        <br />
        {row.campaign_name}
      </MDButton>
    );
  };

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const renderList = () => {
    return (
      <>
        <TableContainer component={Paper} sx={{ minHeight: 400 }}>
          <Table aria-label='datatable table'>
            <TableHeader
              order={order}
              orderBy={orderBy}
              onRequestSort={handleSort}
              data={initHeader}
            />
            <TableBody>
              {record.length === 0 || !productCode ? (
                <TableRow>
                  <TableCell colSpan={initHeader.length} align='center'>
                    ไม่พบข้อมูล
                  </TableCell>
                </TableRow>
              ) : (
                record.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell align='left'>{btnPromotion(row)}</TableCell>
                    <TableCell align='right'>
                      <MDBox className='color-primary'>{Func.numberDisplay(row.quantity)}</MDBox>
                      <MDBox>{row.unit_name}</MDBox>
                    </TableCell>
                    <TableCell align='center'>{Func.dateDisplay(row.date_start)}</TableCell>
                    <TableCell align='center'>{Func.dateDisplay(row.date_stop)}</TableCell>
                    <TableCell className='nowrap'>{promotionDisplay(row.promotion_type)}</TableCell>
                    <TableCell align='right' className='color-info'>
                      {row.promotion_type === 'A' ||
                      row.promotion_type === 'C' ||
                      row.promotion_type === 'D'
                        ? Func.numberDisplay(row.campaign_quantity)
                        : ''}
                    </TableCell>
                    <TableCell align='right' className='color-success'>
                      {Func.numberDisplay(row.price)}
                    </TableCell>
                    <TableCell align='center'>{Func.enableDisplay(row.enable)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Grid item xs={12} sx={{ mt: 3 }}>
          <Card sx={{ p: 2 }}>
            <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
              <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                {t(`element.show_record`, { num: find.perPage })}&nbsp;
                <Icon>keyboard_arrow_down</Icon>
              </MDButton>
              {renderShow}
              <Pagination
                count={pageTotal}
                variant='outlined'
                color='secondary'
                showFirstButton
                showLastButton
                page={pageNo}
                onChange={(e, p) => view({ ...find, page: p })}
              />
            </MDBox>
          </Card>
        </Grid>
      </>
    );
  };

  return <>{renderList()}</>;
}

QueryProductCampaign.propTypes = {
  productCode: PropTypes.string.isRequired,
  setLoading: PropTypes.func.isRequired,
};

export default QueryProductCampaign;
